/**
 * ApiTrackingPage — dash for the API-based tracking surface.
 *
 * Mirrors the BE at apps/api/src/routes/api-tracking.ts. Three panels:
 *   1. Log click — POST /click with campaign_id + optional sub/affiliate/external_id.
 *   2. Log conversion — POST /convert with click_id + optional goal / payouts / external_id.
 *   3. Inspect — GET /click/:id and GET /convert/:id for audit-trail debugging.
 *
 * Brand: green-only headline, Inter, zinc borders, 6-8px rounded,
 * plain Tailwind, no icon libs, no decorative color accents.
 */
import { useState, type FormEvent } from "react";
import {
	useApiTrackingLogClick,
	useApiTrackingLogConvert,
	useApiTrackingClick,
	useApiTrackingConversion,
} from "./hooks";
import type {
	ApiTrackingClickBody,
	ApiTrackingConvertBody,
} from "./api";

const CARD = "rounded-[8px] border border-zinc-200 bg-white p-5 shadow-sm";
const INPUT =
	"w-full rounded-[6px] border border-zinc-300 bg-white px-3 py-2 text-sm text-zinc-900 focus:border-green-500 focus:outline-none focus:ring-2 focus:ring-green-500/20";
const BTN =
	"rounded-[6px] border border-green-600 bg-green-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-green-700 disabled:cursor-not-allowed disabled:opacity-60";
const BTN_GHOST =
	"rounded-[6px] border border-zinc-300 bg-white px-3 py-1.5 text-sm font-medium text-zinc-700 hover:bg-zinc-50";
const LABEL = "flex flex-col gap-1 text-xs font-medium text-zinc-600";

function toNullable(v: string): string | null {
	const t = v.trim();
	return t === "" ? null : t;
}

function toIntCents(v: string): number {
	const t = v.trim();
	if (t === "") return 0;
	const n = Number(t);
	return Number.isFinite(n) && Number.isInteger(n) && n >= 0 ? n : 0;
}

export function ApiTrackingPage() {
	const clickMut = useApiTrackingLogClick();
	const convertMut = useApiTrackingLogConvert();

	// Log-click form state
	const [campaignId, setCampaignId] = useState("");
	const [affiliateId, setAffiliateId] = useState("");
	const [sub1, setSub1] = useState("");
	const [clickExternalId, setClickExternalId] = useState("");

	// Log-convert form state
	const [convertClickId, setConvertClickId] = useState("");
	const [goalId, setGoalId] = useState("");
	const [payoutCents, setPayoutCents] = useState("");
	const [revenueCents, setRevenueCents] = useState("");
	const [convertExternalId, setConvertExternalId] = useState("");

	// Inspector state
	const [lookupClickId, setLookupClickId] = useState<string | null>(null);
	const [lookupConversionId, setLookupConversionId] = useState<string | null>(null);
	const [clickIdInput, setClickIdInput] = useState("");
	const [conversionIdInput, setConversionIdInput] = useState("");

	const clickLookup = useApiTrackingClick(lookupClickId);
	const conversionLookup = useApiTrackingConversion(lookupConversionId);

	const handleLogClick = (e: FormEvent) => {
		e.preventDefault();
		if (!campaignId.trim()) return;
		const body: ApiTrackingClickBody = {
			campaign_id: campaignId.trim(),
			affiliate_id: toNullable(affiliateId),
			sub1: toNullable(sub1),
			external_id: toNullable(clickExternalId),
		};
		clickMut.mutate(body);
	};

	const handleLogConvert = (e: FormEvent) => {
		e.preventDefault();
		if (!convertClickId.trim()) return;
		const body: ApiTrackingConvertBody = {
			click_id: convertClickId.trim(),
			goal_id: toNullable(goalId),
			payout_cents: toIntCents(payoutCents),
			revenue_cents: toIntCents(revenueCents),
			external_id: toNullable(convertExternalId),
		};
		convertMut.mutate(body);
	};

	return (
		<main
			data-testid="api-tracking-page"
			className="mx-auto flex w-full max-w-5xl flex-col gap-8 px-6 py-10"
		>
			<header className="flex flex-col gap-2">
				<p className="text-xs font-semibold uppercase tracking-wider text-zinc-500">
					api-tracking
				</p>
				<h1 className="text-3xl font-semibold text-green-700">
					API-based tracking
				</h1>
				<p className="text-sm text-zinc-600">
					Server-to-server clicks and conversions. JSON in, ids out. No redirect.
				</p>
			</header>

			<section className={CARD} aria-label="Log click">
				<h2 className="text-base font-semibold text-zinc-900">Log click</h2>
				<p className="mt-1 text-xs text-zinc-500">
					POST /api/v1/api-tracking/click
				</p>
				<form
					onSubmit={handleLogClick}
					className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2"
					data-testid="api-tracking-click-form"
				>
					<label className={LABEL}>
						Campaign id
						<input
							type="text"
							className={INPUT}
							value={campaignId}
							onChange={(e) => setCampaignId(e.currentTarget.value)}
							disabled={clickMut.isPending}
							required
							data-testid="api-tracking-campaign-id"
						/>
					</label>
					<label className={LABEL}>
						Affiliate id (optional)
						<input
							type="text"
							className={INPUT}
							value={affiliateId}
							onChange={(e) => setAffiliateId(e.currentTarget.value)}
							disabled={clickMut.isPending}
							data-testid="api-tracking-affiliate-id"
						/>
					</label>
					<label className={LABEL}>
						Sub1 (optional)
						<input
							type="text"
							className={INPUT}
							value={sub1}
							onChange={(e) => setSub1(e.currentTarget.value)}
							disabled={clickMut.isPending}
							data-testid="api-tracking-sub1"
						/>
					</label>
					<label className={LABEL}>
						External id (idempotency, optional)
						<input
							type="text"
							className={INPUT}
							value={clickExternalId}
							onChange={(e) => setClickExternalId(e.currentTarget.value)}
							disabled={clickMut.isPending}
							data-testid="api-tracking-click-external-id"
						/>
					</label>
					<div className="sm:col-span-2 flex items-center gap-3">
						<button
							type="submit"
							className={BTN}
							disabled={clickMut.isPending || !campaignId.trim()}
							data-testid="api-tracking-click-submit"
						>
							{clickMut.isPending ? "Logging." : "Log click"}
						</button>
						{clickMut.data ? (
							<span
								className="text-xs text-zinc-600"
								data-testid="api-tracking-click-result"
							>
								click_id = <span className="font-mono">{clickMut.data.click_id}</span>
							</span>
						) : null}
					</div>
				</form>
				{clickMut.error ? (
					<p
						className="mt-2 text-xs text-red-600"
						role="alert"
						data-testid="api-tracking-click-error"
					>
						{clickMut.error.message}
					</p>
				) : null}
			</section>

			<section className={CARD} aria-label="Log conversion">
				<h2 className="text-base font-semibold text-zinc-900">Log conversion</h2>
				<p className="mt-1 text-xs text-zinc-500">
					POST /api/v1/api-tracking/convert
				</p>
				<form
					onSubmit={handleLogConvert}
					className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2"
					data-testid="api-tracking-convert-form"
				>
					<label className={LABEL}>
						Click id
						<input
							type="text"
							className={INPUT}
							value={convertClickId}
							onChange={(e) => setConvertClickId(e.currentTarget.value)}
							disabled={convertMut.isPending}
							required
							data-testid="api-tracking-convert-click-id"
						/>
					</label>
					<label className={LABEL}>
						Goal id (optional)
						<input
							type="text"
							className={INPUT}
							value={goalId}
							onChange={(e) => setGoalId(e.currentTarget.value)}
							disabled={convertMut.isPending}
							data-testid="api-tracking-goal-id"
						/>
					</label>
					<label className={LABEL}>
						Payout cents (optional)
						<input
							type="number"
							inputMode="numeric"
							min="0"
							step="1"
							className={INPUT}
							value={payoutCents}
							onChange={(e) => setPayoutCents(e.currentTarget.value)}
							disabled={convertMut.isPending}
							data-testid="api-tracking-payout-cents"
						/>
					</label>
					<label className={LABEL}>
						Revenue cents (optional)
						<input
							type="number"
							inputMode="numeric"
							min="0"
							step="1"
							className={INPUT}
							value={revenueCents}
							onChange={(e) => setRevenueCents(e.currentTarget.value)}
							disabled={convertMut.isPending}
							data-testid="api-tracking-revenue-cents"
						/>
					</label>
					<label className={LABEL}>
						External id (idempotency, optional)
						<input
							type="text"
							className={INPUT}
							value={convertExternalId}
							onChange={(e) => setConvertExternalId(e.currentTarget.value)}
							disabled={convertMut.isPending}
							data-testid="api-tracking-convert-external-id"
						/>
					</label>
					<div className="sm:col-span-2 flex items-center gap-3">
						<button
							type="submit"
							className={BTN}
							disabled={convertMut.isPending || !convertClickId.trim()}
							data-testid="api-tracking-convert-submit"
						>
							{convertMut.isPending ? "Logging." : "Log conversion"}
						</button>
						{convertMut.data ? (
							<span
								className="text-xs text-zinc-600"
								data-testid="api-tracking-convert-result"
							>
								conversion_id = <span className="font-mono">{convertMut.data.conversion_id}</span>
							</span>
						) : null}
					</div>
				</form>
				{convertMut.error ? (
					<p
						className="mt-2 text-xs text-red-600"
						role="alert"
						data-testid="api-tracking-convert-error"
					>
						{convertMut.error.message}
					</p>
				) : null}
			</section>

			<section className={CARD} aria-label="Inspect">
				<h2 className="text-base font-semibold text-zinc-900">Inspect</h2>
				<p className="mt-1 text-xs text-zinc-500">
					GET /api/v1/api-tracking/click/:id and /convert/:id
				</p>

				<div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
					<div>
						<label className={LABEL}>
							Click id
							<input
								type="text"
								className={INPUT}
								value={clickIdInput}
								onChange={(e) => setClickIdInput(e.currentTarget.value)}
								data-testid="api-tracking-lookup-click-id"
							/>
						</label>
						<button
							type="button"
							className={`${BTN_GHOST} mt-2`}
							disabled={!clickIdInput.trim()}
							onClick={() => setLookupClickId(clickIdInput.trim() || null)}
							data-testid="api-tracking-lookup-click-submit"
						>
							Fetch click
						</button>
						{lookupClickId ? (
							<div className="mt-3" data-testid="api-tracking-click-panel">
								{clickLookup.isLoading ? (
									<p className="text-sm text-zinc-500" data-testid="api-tracking-click-loading">
										Loading.
									</p>
								) : clickLookup.error ? (
									<p className="text-sm text-red-600" role="alert">
										{clickLookup.error.message}
									</p>
								) : clickLookup.data ? (
									<pre className="overflow-x-auto rounded-[6px] bg-zinc-50 p-3 text-xs text-zinc-800">
										{JSON.stringify(clickLookup.data.click, null, 2)}
									</pre>
								) : null}
							</div>
						) : null}
					</div>

					<div>
						<label className={LABEL}>
							Conversion id
							<input
								type="text"
								className={INPUT}
								value={conversionIdInput}
								onChange={(e) => setConversionIdInput(e.currentTarget.value)}
								data-testid="api-tracking-lookup-conversion-id"
							/>
						</label>
						<button
							type="button"
							className={`${BTN_GHOST} mt-2`}
							disabled={!conversionIdInput.trim()}
							onClick={() => setLookupConversionId(conversionIdInput.trim() || null)}
							data-testid="api-tracking-lookup-conversion-submit"
						>
							Fetch conversion
						</button>
						{lookupConversionId ? (
							<div className="mt-3" data-testid="api-tracking-conversion-panel">
								{conversionLookup.isLoading ? (
									<p className="text-sm text-zinc-500" data-testid="api-tracking-conversion-loading">
										Loading.
									</p>
								) : conversionLookup.error ? (
									<p className="text-sm text-red-600" role="alert">
										{conversionLookup.error.message}
									</p>
								) : conversionLookup.data ? (
									<pre className="overflow-x-auto rounded-[6px] bg-zinc-50 p-3 text-xs text-zinc-800">
										{JSON.stringify(conversionLookup.data.conversion, null, 2)}
									</pre>
								) : null}
							</div>
						) : null}
					</div>
				</div>

				{!lookupClickId && !lookupConversionId ? (
					<p
						className="mt-4 text-sm text-zinc-500"
						data-testid="api-tracking-empty"
					>
						Enter an id above to inspect an event.
					</p>
				) : null}
			</section>
		</main>
	);
}

export default ApiTrackingPage;
